import { z } from 'zod';

const id = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/);
const path = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-zA-Z0-9_.]+$/);

/** Optional business state; a module is not a separate speaking agent. */
export const conversationFlowSchema = z
  .object({
    enabled: z.boolean().default(false),
    version: z.string().min(1).max(64),
    modules: z
      .array(
        z.object({
          id,
          owner: id,
          description: z.string().min(1).max(300),
          scope: z.enum(['call', 'entity']).default('call'),
          required_facts: z.array(id).max(12).default([]),
          confirmation: z.boolean().default(true),
          depends_on: z.array(id).max(20).default([]),
          on_demand: z.boolean().default(false),
          allow_decline: z.boolean().default(true),
          not_applicable_cues: z.array(z.string().min(2).max(100)).max(10).default([]),
          communication_cues: z.array(z.string().min(2).max(100)).max(10).default([]),
          required_actions: z.array(id).max(10).default([]),
        }),
      )
      .min(1)
      .max(30),
    actions: z
      .array(
        z.object({
          id,
          tool: z.string().min(1).max(100),
          /** All matches bind an actual tool argument to the current fact value. */
          matches: z
            .array(z.object({ argument: path, fact: id, scope: z.enum(['call', 'entity']) }))
            .min(1)
            .max(12),
          success_path: path,
          success_value: z.union([z.string().max(100), z.boolean(), z.number()]),
          requires_modules: z.array(id).max(20).default([]),
        }),
      )
      .max(30)
      .default([]),
    handoff_requirements: z.record(z.string(), z.array(id).max(20)).default({}),
  })
  .superRefine((flow, ctx) => {
    const modules = new Map(flow.modules.map((m) => [m.id, m]));
    const actions = new Set(flow.actions.map((a) => a.id));
    const error = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    if (modules.size !== flow.modules.length) error('Module IDs must be unique');
    if (actions.size !== flow.actions.length) error('Action IDs must be unique');
    for (const module of flow.modules) {
      if (new Set(module.required_facts).size !== module.required_facts.length)
        error(`${module.id}: fact IDs must be unique`);
      for (const dep of module.depends_on)
        if (!modules.has(dep)) error(`Unknown dependency: ${dep}`);
      for (const action of module.required_actions)
        if (!actions.has(action)) error(`Unknown action: ${action}`);
      for (const actionId of module.required_actions) {
        const action = flow.actions.find((a) => a.id === actionId);
        if (
          action &&
          (action.matches.some((m) => m.scope === 'entity') ? 'entity' : 'call') !== module.scope
        )
          error(`${module.id}: required action scope must match module scope`);
      }
      if (module.confirmation && !module.required_facts.length)
        error(`${module.id}: confirmation needs a fact to confirm`);
      if (
        !module.confirmation &&
        !module.communication_cues.length &&
        !module.required_actions.length
      ) {
        error(`${module.id}: completion needs communication cues or action evidence`);
      }
    }
    for (const refs of [
      ...Object.values(flow.handoff_requirements),
      ...flow.actions.map((a) => a.requires_modules),
    ]) {
      for (const ref of refs) if (!modules.has(ref)) error(`Unknown required module: ${ref}`);
    }
    const visiting = new Set<string>();
    for (const action of flow.actions) {
      for (const match of action.matches) {
        if (
          !flow.modules.some(
            (m) => m.scope === match.scope && m.required_facts.includes(match.fact),
          )
        )
          error(`Action ${action.id} references unknown fact: ${match.fact}`);
      }
    }
    const visited = new Set<string>();
    const visit = (key: string): boolean => {
      if (visiting.has(key)) return false;
      if (visited.has(key)) return true;
      visiting.add(key);
      const module = modules.get(key);
      const dependencies = [
        ...(module?.depends_on ?? []),
        ...(module?.required_actions ?? []).flatMap(
          (actionId) => flow.actions.find((a) => a.id === actionId)?.requires_modules ?? [],
        ),
      ];
      for (const dep of dependencies) if (!visit(dep)) return false;
      visiting.delete(key);
      visited.add(key);
      return true;
    };
    for (const key of modules.keys())
      if (!visit(key)) {
        error('Module dependencies must not contain cycles');
        break;
      }
  });

export type ConversationFlowPolicy = z.infer<typeof conversationFlowSchema>;
